import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  deleteAppraisalProgressAttachment,
  fetchAppraisalProgressAttachments,
  uploadAppraisalProgressAttachments,
} from "actions";
import {
  getAppraisalAttachmentFileName,
  getAppraisalAttachmentId,
} from "pages/AppraisalManagment/utils/appraisalAttachmentHelpers";
import ObjectiveSnapsThumb from "./ObjectiveSnapsThumb";
import "./ObjectiveSnapsPanel.css";

const SECTION_CONTEXT = "after_objective";
const MAX_FILES_PER_UPLOAD = 20;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
]);

const isAllowedImageFile = (file) => {
  const mime = String(file?.type || "").toLowerCase();
  if (ALLOWED_MIME_TYPES.has(mime)) return true;
  const name = String(file?.name || "").toLowerCase();
  return /\.(jpe?g|png|gif|webp)$/.test(name);
};

const filterAfterObjectiveAttachments = (list) =>
  (list || []).filter(
    (item) =>
      !item?.section_context || String(item.section_context) === SECTION_CONTEXT
  );

const ObjectiveSnapsPanel = ({
  pafId,
  isReadOnly = false,
  initialAttachments = [],
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState(() =>
    filterAfterObjectiveAttachments(initialAttachments)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadAttachments = useCallback(async () => {
    if (!pafId) {
      setAttachments(filterAfterObjectiveAttachments(initialAttachments));
      return;
    }

    setIsLoading(true);
    try {
      const list = await dispatch(fetchAppraisalProgressAttachments(pafId));
      setAttachments(filterAfterObjectiveAttachments(list));
    } catch {
      setAttachments(filterAfterObjectiveAttachments(initialAttachments));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, initialAttachments, pafId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const validateFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const errors = [];
    const validFiles = [];

    if (files.length > MAX_FILES_PER_UPLOAD) {
      errors.push(`You can upload up to ${MAX_FILES_PER_UPLOAD} images at a time.`);
    }

    files.slice(0, MAX_FILES_PER_UPLOAD).forEach((file) => {
      if (!isAllowedImageFile(file)) {
        errors.push(`${file.name}: use JPEG, PNG, GIF, or WebP.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`${file.name}: must be 10 MB or smaller.`);
        return;
      }
      validFiles.push(file);
    });

    return { validFiles, errors };
  };

  const uploadFiles = async (fileList) => {
    if (isReadOnly || !pafId) return;

    const { validFiles, errors } = validateFiles(fileList);
    if (errors.length) {
      setUploadError(errors.join(" "));
    }
    if (!validFiles.length) return;

    const optimisticPreviews = validFiles.map((file, index) => ({
      attachment_id: `local-${Date.now()}-${index}`,
      file_name: file.name,
      _previewUrl: URL.createObjectURL(file),
    }));

    setIsUploading(true);
    setUploadError("");
    setAttachments((prev) => [...prev, ...optimisticPreviews]);

    try {
      await dispatch(uploadAppraisalProgressAttachments(pafId, validFiles, SECTION_CONTEXT));
      await loadAttachments();
    } catch {
      setAttachments((prev) =>
        prev.filter(
          (item) =>
            !optimisticPreviews.some(
              (preview) => getAppraisalAttachmentId(preview) === getAppraisalAttachmentId(item)
            )
        )
      );
    } finally {
      optimisticPreviews.forEach((item) => {
        if (item._previewUrl) {
          URL.revokeObjectURL(item._previewUrl);
        }
      });
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (attachmentId) => {
    if (isReadOnly || !pafId || !attachmentId) return;

    setDeletingId(attachmentId);
    try {
      await dispatch(deleteAppraisalProgressAttachment(pafId, attachmentId));
      setAttachments((prev) =>
        prev.filter((item) => getAppraisalAttachmentId(item) !== attachmentId)
      );
    } catch {
      // Errors surfaced via snackbar in action.
    } finally {
      setDeletingId(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isReadOnly && pafId) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (isReadOnly || !pafId || isUploading) return;
    uploadFiles(event.dataTransfer?.files);
  };

  const canUpload = Boolean(pafId) && !isReadOnly;

  return (
    <div className="my-3 objective-snaps-panel">
      <p className="font-13 font-weight-normal heading-color mb-2">
        Attachments (Snaps / images)
      </p>
      <p className="font-12 text-muted mb-2">
        Upload photos or screenshots while marking this appraisal. Up to {MAX_FILES_PER_UPLOAD}{" "}
        images per upload (max 10 MB each). Formats: JPEG, PNG, GIF, WebP.
      </p>

      {!pafId ? (
        <div className="alert alert-info py-2 mb-2 font-13">
          Save this appraisal first to attach snaps and images.
        </div>
      ) : null}

      {canUpload ? (
        <div
          className={`objective-snaps-dropzone mb-3 ${isDragging ? "objective-snaps-dropzone--drag" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple
            className="d-none"
            onChange={(event) => uploadFiles(event.target.files)}
            disabled={isUploading}
          />
          <button
            type="button"
            className="n-u-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? "Uploading…" : "Choose images"}
          </button>
          <span className="font-12 text-muted ms-2">or drag and drop here</span>
        </div>
      ) : null}

      {uploadError ? <div className="text-danger font-12 mb-2">{uploadError}</div> : null}

      {isLoading ? (
        <p className="font-13 text-muted mb-0">Loading attachments…</p>
      ) : attachments.length ? (
        <div className="objective-snaps-grid">
          {attachments.map((item) => {
            const attachmentId = getAppraisalAttachmentId(item);
            const label = getAppraisalAttachmentFileName(item);
            return (
              <div key={attachmentId || label} className="objective-snaps-card">
                <ObjectiveSnapsThumb attachment={item} label={label} />
                <div className="objective-snaps-meta">
                  <span className="objective-snaps-name" title={label}>
                    {label}
                  </span>
                  {canUpload ? (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 objective-snaps-remove"
                      onClick={() => handleDelete(attachmentId)}
                      disabled={deletingId === attachmentId || !attachmentId}
                    >
                      {deletingId === attachmentId ? "Removing…" : "Remove"}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="font-13 text-muted mb-0">No snaps uploaded yet.</p>
      )}
    </div>
  );
};

export default ObjectiveSnapsPanel;
